import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Toaster } from "../components/ui/sonner";
import { toast } from "sonner";
import { Loader2, LogOut } from "lucide-react";

import DemoBanner from "../components/app/DemoBanner";
import TrialBanner from "../components/app/TrialBanner";
import BrowserWarning from "../components/app/BrowserWarning";
import AppHeader from "../components/app/AppHeader";
import PatientSidebar from "../components/app/PatientSidebar";
import PatientPicker from "../components/app/PatientPicker";
import PatientDashboard from "../components/app/PatientDashboard";
import PlanEditor from "../components/app/PlanEditor";
import AllReportsView from "../components/app/AllReportsView";
import NewPlanDialog from "../components/app/NewPlanDialog";
import PlanReadOnlyDialog from "../components/app/PlanReadOnlyDialog";
import OnboardingModal from "../components/app/OnboardingModal";

import { useAuth } from "../contexts/AuthContext";
import * as api from "../lib/api";
import { EVAL_SLOTS, DAILY_SLOTS } from "../lib/therapyTypes";

function mergeAudioUrls(prevPatients, plan, audioMap) {
  // attach audioUrl from blob URLs in audioMap by storage path
  const apply = (section) => {
    if (!section) return section;
    const url = audioMap[section.audio_path];
    return url ? { ...section, audioUrl: url } : section;
  };
  return {
    ...plan,
    patient: apply(plan.patient),
    evaluation: apply(plan.evaluation),
    daily: apply(plan.daily),
  };
}

export default function AppPage() {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();

  const [patients, setPatients] = useState([]);
  const [plansByPatient, setPlansByPatient] = useState({}); // patient_id -> [plans]
  const [selectedPatientId, setSelectedPatientId] = useState(null);
  const [view, setView] = useState("empty");
  const [currentPlan, setCurrentPlan] = useState(null);
  const [newPlanOpen, setNewPlanOpen] = useState(false);
  const [readOnlyPlan, setReadOnlyPlan] = useState(null);
  const [readOnlyOrigin, setReadOnlyOrigin] = useState("patient");
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [audioBlobUrls, setAudioBlobUrls] = useState({}); // storage_path -> blob URL
  const [saveStatus, setSaveStatus] = useState("idle"); // idle | saving | saved | error

  const saveTimeoutRef = useRef(null);
  const planForSaveRef = useRef(null);

  const selectedPatient = useMemo(() => {
    const p = patients.find((x) => x.id === selectedPatientId);
    if (!p) return null;
    const plans = plansByPatient[p.id] || [];
    return { ...p, plans };
  }, [patients, selectedPatientId, plansByPatient]);

  useEffect(() => {
    document.title = "TherapyNote";
  }, []);

  // Initial fetch
  useEffect(() => {
    (async () => {
      try {
        const list = await api.listPatients();
        setPatients(list.map((p) => ({ id: p.id, initials: p.initials, plan_count: p.plan_count })));
      } catch (e) {
        toast.error("Couldn't load patients", { description: e.message });
      }
    })();
  }, []);

  const ensurePatientPlansLoaded = useCallback(async (patientId) => {
    if (plansByPatient[patientId]) return;
    setLoadingPlans(true);
    try {
      const list = await api.listPlansForPatient(patientId);
      setPlansByPatient((prev) => ({ ...prev, [patientId]: list }));
    } catch (e) {
      toast.error("Couldn't load plans", { description: e.message });
    } finally {
      setLoadingPlans(false);
    }
  }, [plansByPatient]);

  const selectPatient = useCallback(
    async (id) => {
      setSelectedPatientId(id);
      setCurrentPlan(null);
      setView("patient");
      await ensurePatientPlansLoaded(id);
    },
    [ensurePatientPlansLoaded]
  );

  const handleOpenNewPlanDialog = () => setNewPlanOpen(true);

  const startEditingPlan = (plan) => {
    setCurrentPlan(plan);
    setSelectedPatientId(plan.patient_id);
    setView("editing");
  };

  const handleNewPlanFromDashboard = async () => {
    if (!selectedPatient) {
      toast.error("Select a patient first.");
      return;
    }
    if (!user.can_save) {
      toast.error("Trial expired", {
        description: "Subscribe to create new plans.",
      });
      return;
    }
    try {
      const plan = await api.createPlan({
        patient_id: selectedPatient.id,
        initials: selectedPatient.initials,
        type: "evaluation",
      });
      setPlansByPatient((prev) => ({
        ...prev,
        [plan.patient_id]: [plan, ...(prev[plan.patient_id] || [])],
      }));
      setPatients((prev) =>
        prev.map((p) =>
          p.id === plan.patient_id ? { ...p, plan_count: (p.plan_count || 0) + 1 } : p
        )
      );
      startEditingPlan(plan);
    } catch (e) {
      toast.error("Couldn't create plan", { description: e.message });
    }
  };

  const handleCreatePlan = async ({ id, initials }) => {
    if (!user.can_save) {
      toast.error("Trial expired", { description: "Subscribe to create new plans." });
      return;
    }
    try {
      const plan = await api.createPlan({
        patient_id: id,
        initials,
        type: "evaluation",
      });
      // Make sure patient is in list
      setPatients((prev) => {
        if (prev.find((p) => p.id === id)) {
          return prev.map((p) =>
            p.id === id ? { ...p, initials, plan_count: (p.plan_count || 0) + 1 } : p
          );
        }
        return [{ id, initials, plan_count: 1 }, ...prev];
      });
      setPlansByPatient((prev) => ({
        ...prev,
        [id]: [plan, ...(prev[id] || [])],
      }));
      setNewPlanOpen(false);
      toast.success(`Plan started for ${id}`);
      startEditingPlan(plan);
    } catch (e) {
      toast.error("Couldn't start plan", { description: e.message });
    }
  };

  // Debounced auto-save on plan change
  // Note: flushSave is defined below — we read it lazily via ref to avoid TDZ.
  const flushSaveRef = useRef(null);
  const handlePlanChange = useCallback((updated) => {
    setCurrentPlan(updated);
    planForSaveRef.current = updated;
    setSaveStatus("saving");
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      flushSaveRef.current?.(updated, /* silent */ true);
    }, 1200);
  }, []);

  const flushSave = useCallback(async (plan, silent = false) => {
    if (!plan?.id) return;
    if (!user?.can_save) {
      if (!silent)
        toast.error("Trial expired", {
          description: "Notes can be viewed but not saved. Subscribe to keep saving.",
        });
      return;
    }
    setSaveStatus("saving");
    try {
      const evalPatch = {
        transcript: plan.evaluation?.transcript || "",
      };
      EVAL_SLOTS.forEach((k) => {
        evalPatch[k] = plan.evaluation?.[k] || "";
      });
      const dailyPatch = {
        transcript: plan.daily?.transcript || "",
      };
      DAILY_SLOTS.forEach((k) => {
        dailyPatch[k] = plan.daily?.[k] || "";
      });
      const patch = {
        patient: {
          transcript: plan.patient?.transcript || "",
          summary: plan.patient?.summary || "",
        },
        evaluation: evalPatch,
        daily: dailyPatch,
      };
      const saved = await api.updatePlan(plan.id, patch);
      setPlansByPatient((prev) => ({
        ...prev,
        [saved.patient_id]: (prev[saved.patient_id] || []).map((p) =>
          p.id === saved.id ? { ...saved, ...mergeAudioCarry(plan, saved) } : p
        ),
      }));
      setSaveStatus("saved");
      if (!silent) toast.success("Saved");
    } catch (e) {
      setSaveStatus("error");
      if (!silent) toast.error("Couldn't save", { description: e.message });
    }
  }, [user]);

  // Keep ref in sync so handlePlanChange can call the latest flushSave
  flushSaveRef.current = flushSave;

  function mergeAudioCarry(currentPlan, saved) {
    // Preserve in-memory audioUrl (local blob URL) on top of server response
    return {
      patient: { ...saved.patient, audioUrl: currentPlan.patient?.audioUrl || null },
      evaluation: { ...saved.evaluation, audioUrl: currentPlan.evaluation?.audioUrl || null },
      daily: { ...saved.daily, audioUrl: currentPlan.daily?.audioUrl || null },
    };
  }

  const handleSavePlan = (plan) => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    flushSaveRef.current?.(plan, false);
  };

  const handleAudioCaptured = useCallback(async (tab, blob) => {
    const plan = planForSaveRef.current || currentPlan;
    if (!plan?.id || !user?.can_save) return;
    try {
      const result = await api.uploadAudio({
        plan_id: plan.id,
        tab,
        blob,
        filename: `${tab}-${Date.now()}.webm`,
      });
      // Update plan with audio_path and keep local audioUrl for instant playback
      setCurrentPlan((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          [tab]: { ...prev[tab], audio_path: result.audio_path },
        };
      });
      toast.success("Audio saved", {
        description: `${tab.charAt(0).toUpperCase() + tab.slice(1)} audio uploaded.`,
      });
    } catch (e) {
      toast.error("Audio upload failed", { description: e.message });
    }
  }, [currentPlan, user]);

  const handleBackToPatient = () => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      if (planForSaveRef.current) flushSaveRef.current?.(planForSaveRef.current, true);
    }
    setCurrentPlan(null);
    setView("patient");
  };

  const handleDismissVoiceHint = useCallback(async () => {
    // Optimistically hide
    setUser((prev) => (prev ? { ...prev, voice_hint_dismissed: true } : prev));
    try {
      const updated = await api.updateProfile({ voice_hint_dismissed: true });
      setUser(updated);
    } catch (e) {
      // Best-effort — leave the optimistic update in place
    }
  }, [setUser]);

  const handleDismissDailyHint = useCallback(async () => {
    setUser((prev) => (prev ? { ...prev, daily_hint_dismissed: true } : prev));
    try {
      const updated = await api.updateProfile({ daily_hint_dismissed: true });
      setUser(updated);
    } catch (e) {
      // best-effort
    }
  }, [setUser]);

  const handleUpdateReadOnlyField = useCallback(async (planId, section, key, value) => {
    if (!user?.can_save) {
      throw new Error("Your trial has expired. Subscribe to keep editing notes.");
    }
    // Build a section patch that only touches this one field.
    const patch = { [section]: { [key]: value } };
    const saved = await api.updatePlan(planId, patch);
    // Reflect in plansByPatient
    setPlansByPatient((prev) => ({
      ...prev,
      [saved.patient_id]: (prev[saved.patient_id] || []).map((p) =>
        p.id === saved.id ? { ...p, ...saved } : p
      ),
    }));
    // Reflect in the dialog itself so the user sees the updated value immediately.
    setReadOnlyPlan((prev) => (prev && prev.id === saved.id ? { ...prev, ...saved } : prev));
    return saved;
  }, [user]);

  const openReadOnly = async (plan, origin = "patient") => {
    setReadOnlyOrigin(origin);
    setReadOnlyPlan(plan);
    // Lazy-fetch audio blobs for any audio_path in the plan
    const paths = [plan.patient?.audio_path, plan.evaluation?.audio_path, plan.daily?.audio_path].filter(Boolean);
    for (const path of paths) {
      if (audioBlobUrls[path]) continue;
      try {
        const url = await api.fetchAudioBlobUrl(path);
        setAudioBlobUrls((prev) => ({ ...prev, [path]: url }));
      } catch (e) {
        // ignore
      }
    }
  };

  const goToReports = async () => {
    setView("reports");
    setCurrentPlan(null);
    // Eagerly fetch every plan across all patients — the by-patient map is
    // populated lazily as the user opens each patient, so if they land here
    // first it would otherwise show empty.
    try {
      const all = await api.listAllPlans();
      setPlansByPatient((prev) => {
        const grouped = { ...prev };
        all.forEach((p) => {
          const list = grouped[p.patient_id] || [];
          const idx = list.findIndex((x) => x.id === p.id);
          if (idx >= 0) list[idx] = p;
          else list.push(p);
          grouped[p.patient_id] = list;
        });
        return grouped;
      });
    } catch (e) {
      toast.error("Couldn't load reports", { description: e.message });
    }
  };

  const goToPatientHistory = () => {
    if (!selectedPatient) {
      toast.error("Select a patient first to see their history.");
      return;
    }
    setCurrentPlan(null);
    setView("patient");
  };

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  const allPatientsForReports = useMemo(() => {
    return patients.map((p) => ({
      id: p.id,
      initials: p.initials,
      plans: plansByPatient[p.id] || [],
    }));
  }, [patients, plansByPatient]);

  const readOnlyWithAudio = readOnlyPlan
    ? mergeAudioUrls(patients, readOnlyPlan, audioBlobUrls)
    : null;

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-[#0F172A]" data-testid="app-page">
      <Toaster position="top-center" richColors />
      <TrialBanner user={user} />
      <BrowserWarning />
      <AppHeader
        selectedPatient={selectedPatient}
        onPatientHistory={goToPatientHistory}
        onAllReports={goToReports}
        onLogoClick={() => navigate("/")}
        rightExtra={
          <button
            data-testid="app-logout-btn"
            onClick={handleLogout}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full font-figtree text-xs text-[#475569] hover:text-[#EF4444] hover:bg-red-50 transition-colors"
            title={user?.email || "Account"}
          >
            <LogOut className="w-3.5 h-3.5" />
            Sign out
          </button>
        }
        user={user}
      />

      <div className="max-w-7xl mx-auto px-4 md:px-8 pt-6 pb-16 grid grid-cols-1 lg:grid-cols-12 gap-6">
        <aside className="lg:col-span-3">
          <PatientSidebar
            patients={patients}
            selectedPatientId={selectedPatientId}
            onSelect={selectPatient}
            onNewPlan={handleOpenNewPlanDialog}
          />
        </aside>

        <main className="lg:col-span-9">
          {view === "empty" && (
            <PatientPicker
              onSelect={selectPatient}
              onNewPlan={handleOpenNewPlanDialog}
              patients={patients}
            />
          )}

          {view === "patient" && selectedPatient && (
            loadingPlans ? (
              <div className="bg-white rounded-3xl border border-slate-200/80 shadow-soft p-12 text-center">
                <Loader2 className="w-6 h-6 animate-spin text-[#10B981] mx-auto" />
              </div>
            ) : (
              <PatientDashboard
                patient={selectedPatient}
                onNewPlan={handleNewPlanFromDashboard}
                onOpenPlan={(plan) => openReadOnly(plan, "patient")}
              />
            )
          )}

          {view === "editing" && selectedPatient && currentPlan && (
            <PlanEditor
              patient={selectedPatient}
              plan={currentPlan}
              onChange={handlePlanChange}
              onSave={handleSavePlan}
              onBack={handleBackToPatient}
              onAudioCaptured={handleAudioCaptured}
              saveStatus={saveStatus}
              therapyType={user?.therapy_type || "physiotherapy"}
              showVoiceHint={user && user.voice_hint_dismissed === false}
              onDismissVoiceHint={handleDismissVoiceHint}
              showDailyHint={user && user.daily_hint_dismissed === false}
              onDismissDailyHint={handleDismissDailyHint}
            />
          )}

          {view === "reports" && (
            <AllReportsView
              patients={allPatientsForReports}
              onOpenPlan={(plan, patient) => {
                setSelectedPatientId(patient.id);
                openReadOnly(plan, "reports");
              }}
            />
          )}
        </main>
      </div>

      <NewPlanDialog
        open={newPlanOpen}
        onOpenChange={setNewPlanOpen}
        onCreate={handleCreatePlan}
        existingIds={patients.map((p) => p.id)}
      />

      <PlanReadOnlyDialog
        plan={readOnlyWithAudio}
        patient={selectedPatient}
        onClose={() => setReadOnlyPlan(null)}
        onUpdate={handleUpdateReadOnlyField}
        canEdit={!!user?.can_save}
        originLabel={readOnlyOrigin === "reports" ? "All Reports" : "Patient History"}
        therapyType={user?.therapy_type || "physiotherapy"}
      />

      {user && user.onboarding_complete === false && (
        <OnboardingModal
          open={true}
          defaultValue={user.therapy_type}
          onComplete={(updatedUser) => setUser(updatedUser)}
        />
      )}
    </div>
  );
}
